// `ChatOverlay` — the Beekeeper conversation as an **inset popup** (`client-beekeeper-chat` "The
// overlay is a geometry-independent full-screen layer", design **D9** /
// `sandbox-screen-consolidation/design.md` §4.2; sketch
// `prototype/sketches/sandbox-screen-consolidation/chat-modal.html`, geometry in the change's
// visual spec §7). The surface `PromptBox` opens into (`client-colony` "the prompt control opens
// the chat overlay").
//
// **Why a popup and not a screen.** It floats with a uniform 16px margin on every side, rounded on
// all four corners, `--shadow-sheet`, and **no scrim**. The world and its bees keep animating in
// every margin and the control stack shows along the bottom — the conversation reads as a card
// held *over* the game rather than as somewhere the player was taken. The absence of a scrim is
// load-bearing, not decorative: the visible activity behind it is the point.
//
// Unlike `TypeSheet`, which stops 228px short of the bottom to leave the three control bars usable
// beneath it, this popup takes the **full frame minus the margin** — a conversation needs the room,
// and the stack showing through the bottom margin is enough to keep the loop's next step visible.
// Its own input therefore lives in its footer, landing within a few pixels of where `PromptBox`
// sits on the screen behind, so the box reads as *growing into* the popup rather than jumping to a
// new place.
//
// **Geometry independence (design T7).** A `position: fixed` layer that measures no dock, bar,
// sheet or map coordinate — no `getBoundingClientRect`, no `ResizeObserver`, no portal — the same
// D-086/D-087 property `FirstRunGuide` relies on: header, transcript and footer are provable in
// jsdom under zero layout, and the popup is immune to any content-box clip. The inset and the
// radius are *style*, never measurement.
//
// **Where the two controls live** (`player-chat-and-outcome` **D-1**/**D-2**, 2026-08-12). Stop and
// Close used to be the two branches of ONE header conditional, so Stop appeared where Close was and
// displaced it: a player reaching to dismiss the panel found Stop, and a player reaching to stop the
// agent hunted for a control that had moved. They are now two controls in two places.
//
//   * **Stop is in the footer**, replacing Send while `phase === 'running'` — beside the textarea
//     the player is already looking at. Its own element, its own danger styling and its own
//     `aria-label`, never a relabel of Send.
//   * **Close is in the header, unconditionally** — including mid-turn, because it no longer ends
//     the conversation. Every mount wires `onClose` to its own *minimise*: the overlay's body
//     unmounts while the chat session, its SSE subscription and any in-flight turn continue
//     untouched, and `ChatReentryRow` becomes the live report (**D-3**). Ending a conversation
//     outright is the owner's `close()`, reached by an explicit act (a new match, a session reset),
//     never by this button and never by the panic key.
//
// The input stays disabled for the whole of busy. Escape maps to "stop, then dismiss once the turn
// has actually stopped" (`pendingAutoCloseRef`) — and since `onClose` minimises, that is now "stop,
// then minimise", never a silent dismiss of a still-running turn. Focus is trapped while mounted —
// two off-screen sentinels bracket the real controls, so tabbing past either end wraps via the DOM's
// own tab order rather than a hand-rolled Tab interception that would have to fight `userEvent`'s
// tab-order computation. **The trap is released by the unmount itself**: minimising removes this
// whole subtree, sentinels included, so no trap can survive around an unrendered panel — which is
// why minimise is an owner-side state change rather than a `display: none` here. (`TypeSheet`
// deliberately does neither: it is a card over a live stack, this is genuinely modal.)
//
// **Following the conversation (WAG-95).** A running turn appends to the transcript continuously,
// so the overlay pulls the view down with it — but only while the player is *already* at the
// bottom. Scrolling up to re-read something mid-turn must not be undone by the next chunk, so the
// stickiness is recorded from the container's own `scroll` events and never recomputed at append
// time: by then the freshly-rendered content has already moved the bottom away from wherever the
// player was, and every append would read as "not following". When it is off, a jump-to-latest
// control appears over the transcript and turns it back on — the same affordance every agent chat
// offers, and the only way back other than dragging.
//
// This reads `scrollTop`/`scrollHeight`/`clientHeight` on the component's OWN scroll box, which is
// not a breach of the geometry independence above: that rule is about *external* geometry — no
// rectangle is measured, no `ResizeObserver` is constructed, and nothing outside this popup is
// consulted. The consequence of owning no observer is stated rather than hidden: content that grows
// without the transcript changing (a tool row the player expands) does not re-pull the view, which
// is correct — that growth is the player's own act, not the agent's.
//
// **Agent prose is delegated, never re-implemented.** `AgentProse` (`agent-prose.tsx`) is the
// sanitized-markdown renderer of design **T5**/§5: it emits a closed set of element types and has
// no code path that can produce an anchor, an image, a script or a raw-HTML sink, for any input.
// This file must never grow a second, weaker rendering path beside it — no `dangerouslySetInnerHTML`
// and no markup assembled from `m.text` here.

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import type { TranscriptItem } from '../../chat/transcript-model';
import { submitOnEnter } from '../../keyboard-policy';
import { isChatBusy, type ChatPhase } from '../../colony/chat-seam';
import { AgentProse } from './agent-prose';
import { ToolCallRow } from './ToolCallRow';
import { TerminalRow } from './TerminalRow';
import { BusyBee } from './BusyBee';

/** The uniform margin on every side (visual spec §7.1). Style, not a measurement. */
export const CHAT_OVERLAY_INSET_PX = 16;

export interface ChatOverlayProps {
  transcript: readonly TranscriptItem[];
  /**
   * The channel's own turn phase (`beekeeper-progress-indicator` §1/§2) — drives the header's
   * status text/tint/bee, whether the FOOTER offers Send or Stop (**D-1**), and whether the
   * composer is disabled. The header's control no longer varies with it at all.
   * `running` used to be a bare boolean here; the modality split it drove (Stop vs Close, composer
   * disabled) is unchanged, it is just `phase === 'running'` now rather than a prop of its own,
   * because `submitting` and `uncertain` need their own header treatment too and neither is
   * `running`.
   */
  phase: ChatPhase;
  /**
   * True from the moment a send goes out until the turn's first output — drives the no-box
   * "Thinking" placeholder (sketch variant **C**, decided 2026-08-10) directly in the transcript.
   * Rendered as `isChatBusy(phase) && awaitingFirstOutput`, so a turn that settles having produced
   * no output at all cannot strand it. Defaults to `false` for callers that do not track it.
   */
  awaitingFirstOutput?: boolean;
  /** True only alongside `phase === 'failed'` when that outcome was a settled turn that produced
   * NO agent output at all (`beekeeper-progress-indicator` review, H1) — swaps the header's generic
   * refusal copy for an honest "that didn't reach the Beekeeper" line, since the generic line
   * ("nothing was admitted") would be false here: the turn was admitted and ran, it simply said
   * nothing back. Defaults to `false` for callers (and phases) that do not track it. */
  noOutputSettle?: boolean;
  /** Overrides the default header subtitle for `idle` only (e.g. "Current selected type: nurse").
   * Every other phase states its own status text, which takes priority — a player mid-send needs to
   * see that over a scope annotation. */
  statusLine?: string;
  /**
   * The bee type the prompt is currently scoped to, or omitted for the whole colony. Rendered as
   * its own pill row above the player's most recent message — the copy is present-tense ("Current
   * selected type"), so it annotates the message just sent under this scope rather than being
   * stamped onto history the transcript carries no scope for.
   */
  scopeTypeName?: string | null;
  /**
   * The rounds G5 phase clock's bare-remainder mounting (`client-rounds-phase`,
   * `client-sandbox-screen`'s "the header of each inset popup" requirement) — rendered in this
   * header beside the existing Stop/Close control, per
   * `prototype/sketches/rounds-client/phase-clock-slot.html`'s option D. A slot rather than a
   * hard dependency on `PhaseClock`: live mode's Colony screen opens this same overlay and passes
   * nothing, so nothing renders here for it.
   */
  headerRemainder?: ReactNode;
  onSend(text: string): void;
  onStop(): void;
  /**
   * The header's ✕ — and Escape's destination once a running turn has stopped. **Every mount wires
   * this to its own `minimise`, never to a teardown** (`player-chat-and-outcome` **D-2**): the
   * conversation, its stream and any in-flight turn survive, and the player gets them back from
   * `ChatReentryRow`. Named for what the panel does (it goes away), not for what the owner does
   * with the state — this component stays presentational and holds no lifecycle of its own.
   */
  onClose(): void;
  /**
   * "Refresh conversation" — the `uncertain` phase's only affordance, and the only way it resolves
   * (`beekeeper-progress-indicator` §1: evidence via an on-demand `rehydrate()`, never a timer).
   * Optional so a caller that never reaches `uncertain` (a bare mount, most tests) can omit it; the
   * button still renders whenever `phase === 'uncertain'`, it just no-ops without a real handler.
   */
  onRefresh?(): void;
  /**
   * "New chat" — clears this conversation and starts a new one (WAG-94), rendered in the header
   * beside Close.
   *
   * Optional, and its absence removes the control rather than disabling it: a surface whose channel
   * cannot clear (the presentation's read-only preview — `ChatSeam.clearConversation`) must not
   * offer a button that could only fail. It is also withheld for the whole of `busy`, for the same
   * reason Close is withheld while `running`: the server refuses a clear that races a turn, and a
   * transcript still being written is not one to throw away behind the turn's back.
   */
  onClear?(): void;
}

const OVERLAY_STYLE: CSSProperties = {
  position: 'fixed',
  top: CHAT_OVERLAY_INSET_PX,
  left: CHAT_OVERLAY_INSET_PX,
  right: CHAT_OVERLAY_INSET_PX,
  bottom: CHAT_OVERLAY_INSET_PX,
  // Above the bee-type sheet (20) and `FirstRunGuide` (40 — CONTRACT.md §7: "z-index above 40").
  zIndex: 50,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'var(--surface-app)',
  border: '1.5px solid var(--hud-line)',
  borderRadius: 'var(--r-lg)',
  boxShadow: 'var(--shadow-sheet)',
};

// Deliberately `absolute`, not `fixed`: the popup is the only fixed layer this component
// contributes, which is how "no scrim" is asserted — a fixed sentinel would look like one.
const SENTINEL_STYLE: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

const HEADER_STYLE: CSSProperties = {
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  padding: '10px 12px',
  background: 'var(--hud-bg)',
  borderBottom: '1px solid var(--line)',
};

const TITLE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 17,
  color: 'var(--ink-1)',
  lineHeight: 1,
};

/** The transcript's own region: a positioned wrapper so the jump-to-latest control can float over
 * the conversation's bottom-right corner without being scrolled away by the content it points at,
 * and without measuring the footer's height to clear it. */
const TRANSCRIPT_REGION_STYLE: CSSProperties = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const SCROLL_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: 'var(--sp-3)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

/** `absolute`, deliberately never `fixed`: the popup itself is the only fixed layer this component
 * contributes (the no-scrim rule the inset-popup test asserts as a count). */
const JUMP_TO_LATEST_STYLE: CSSProperties = {
  position: 'absolute',
  right: 'var(--sp-3)',
  bottom: 'var(--sp-3)',
  width: 'var(--hit-target)',
  height: 'var(--hit-target)',
  minWidth: 'var(--hit-target)',
  minHeight: 'var(--hit-target)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: 'var(--surface-card)',
  color: 'var(--ink-2)',
  border: '1.5px solid var(--line-strong)',
  // The kit's journal offset shadow, not `--shadow-sheet` (which throws upward, for a sheet rising
  // from the bottom edge) — this is a small card lifted off the conversation.
  boxShadow: 'var(--shadow-hud)',
  fontFamily: 'var(--font-display)',
  fontSize: 17,
  lineHeight: 1,
  cursor: 'pointer',
};

/** Its own row above the bubble, pulled 6px into it so the two read as one unit. */
const SCOPE_PILL_STYLE: CSSProperties = {
  alignSelf: 'flex-end',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 10px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--paper-1)',
  border: '1px solid var(--line-strong)',
  fontFamily: 'var(--font-body)',
  fontSize: 10,
  color: 'var(--ink-3)',
  marginBottom: -6,
};

/** All B++ vocabulary is mono, and a bee-type name is B++ vocabulary. */
const SCOPE_TYPE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  color: 'var(--accent-strong)',
};

const PLAYER_BUBBLE_STYLE: CSSProperties = {
  alignSelf: 'flex-end',
  maxWidth: '84%',
  background: 'var(--honey-200)',
  border: '1px solid var(--honey-300)',
  borderRadius: 'var(--r-md)',
  padding: '8px 11px',
  fontSize: 'var(--text-sm)',
  lineHeight: 'var(--leading-body)',
  color: 'var(--ink-1)',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
};

const AGENT_ROW_STYLE: CSSProperties = { display: 'flex', gap: 7 };

const AGENT_MARKER_STYLE: CSSProperties = { flex: 'none', fontSize: 15, lineHeight: 1.5 };

const AGENT_PROSE_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--text-sm)',
  lineHeight: 'var(--leading-body)',
  color: 'var(--ink-1)',
  overflowWrap: 'break-word',
};

/** Tool rows indent to the prose column, not under the 🐝 marker (visual spec §7.3). */
const TOOL_COLUMN_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  marginLeft: 22,
};

/** No box at all (sketch variant **C**) — `align-self: flex-start` and a bare row, same as an agent
 * message's own alignment, but with none of a bubble's background/border/padding. */
const THINKING_PLACEHOLDER_STYLE: CSSProperties = {
  alignSelf: 'flex-start',
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '2px 2px 2px 4px',
};

const THINKING_LABEL_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-xs)',
  color: 'var(--text-muted)',
};

const THINKING_DOTS_STYLE: CSSProperties = {
  display: 'inline-flex',
  gap: 4,
  marginLeft: 2,
};

/** Static, not blinking (a simplification within `beekeeper-progress-indicator` §4's scope: only
 * the bee's own motion is pinned to WAAPI there). The dots are decoration under `aria-hidden`;
 * the bee alone carries the "something is happening" signal. */
const THINKING_DOT_STYLE: CSSProperties = {
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: 'var(--ink-3)',
  display: 'block',
};

const HIT_TARGET_BUTTON: CSSProperties = {
  flex: 'none',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 'var(--text-sm)',
  minHeight: 'var(--hit-target)',
  minWidth: 'var(--hit-target)',
  padding: '0 var(--sp-4)',
  borderRadius: 'var(--r-pill)',
  cursor: 'pointer',
};

const FOOTER_STYLE: CSSProperties = {
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  padding: '8px 10px',
  background: 'var(--hud-bg)',
  borderTop: '1px solid var(--line)',
};

/** `.field` from the kit's prompt bar, verbatim — this is why the box reads as *growing into* the
 * popup: it is the same control in the same place as the screen behind (visual spec §5.1/§7.5).
 *
 * Its **frame** (resting border, radius, focus ring) is the one part not declared here: that comes
 * from the shell's `[data-ui-field='composite']` rules, which the prompt bar behind this popup also
 * uses — so the two fields cannot drift, and the textarea inside cannot draw a second ring of its
 * own. Declaring a border here would additionally outrank the shell's focused accent edge. */
const FIELD_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  minHeight: 'var(--hit-target)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 6px',
  background: 'var(--surface-card)',
};

const INPUT_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 'var(--hit-target)',
  minHeight: 'var(--hit-target)',
  border: 'none',
  outline: 'none',
  resize: 'none',
  background: 'transparent',
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-sm)',
  lineHeight: 'var(--leading-body)',
  color: 'var(--ink-1)',
};

const IDLE_PLACEHOLDER = '🐝 Ask the Beekeeper about your colony…';
const RUNNING_PLACEHOLDER = 'Beekeeper is working — press Stop to interrupt';
const BUSY_PLACEHOLDER = 'The Beekeeper is busy — hold on…';

// The header's phase-driven status text (`beekeeper-progress-indicator` §2's exact copy). `idle`
// answers `null` deliberately — that phase alone still honours a caller's own `statusLine` (or the
// default "Idle · N messages"); every other phase states its own status and takes priority.
const SUBMITTING_STATUS_LINE = 'Contacting the Beekeeper…';
const RUNNING_STATUS_LINE = 'Beekeeper is working…';
const UNCERTAIN_STATUS_LINE = 'Delivery unknown';
const FAILED_STATUS_LINE = 'Send failed — nothing was admitted. Edit and try again.';
// `beekeeper-progress-indicator` review, H1: a settled turn with no agent output at all was
// genuinely admitted and ran — `FAILED_STATUS_LINE`'s "nothing was admitted" would be false here.
// This is the honest alternative for that one specific cause of `failed`.
const NO_OUTPUT_STATUS_LINE = "That didn't reach the Beekeeper — nothing was changed. Edit and try again.";

function phaseStatusLine(phase: ChatPhase, noOutputSettle: boolean): string | null {
  switch (phase) {
    case 'submitting':
      return SUBMITTING_STATUS_LINE;
    case 'running':
      return RUNNING_STATUS_LINE;
    case 'uncertain':
      return UNCERTAIN_STATUS_LINE;
    case 'failed':
      return noOutputSettle ? NO_OUTPUT_STATUS_LINE : FAILED_STATUS_LINE;
    case 'idle':
      return null;
  }
}

function phaseStatusColor(phase: ChatPhase): string {
  switch (phase) {
    case 'submitting':
    case 'running':
      return 'var(--honey-600)';
    case 'uncertain':
      return 'var(--warning)';
    case 'failed':
      return 'var(--danger)';
    case 'idle':
      return 'var(--text-muted)';
  }
}

/** How far from the bottom still counts as being at the bottom. A browser reports fractional scroll
 * metrics under a zoomed or half-pixel layout, so an exact equality would drop out of following for
 * no reason the player did; a line or two of slack is also honestly still "watching the newest
 * message". */
const FOLLOW_SLACK_PX = 24;

function distanceFromBottom(el: HTMLElement): number {
  return el.scrollHeight - el.scrollTop - el.clientHeight;
}

/** Real (non-sentinel) focusable descendants of `container`, in DOM/tab order. */
function realFocusables(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>('button:not(:disabled), textarea:not(:disabled), [tabindex="0"]'),
  ).filter((el) => el.dataset['chatFocusTrapSentinel'] === undefined);
}

function messageCount(transcript: readonly TranscriptItem[]): number {
  return transcript.filter((item) => item.kind === 'message').length;
}

/** Index of the player's most recent message, or -1 — see `scopeTypeName`'s note on why the pill
 * attaches to that one message rather than to every player bubble in the thread. */
function lastPlayerMessageIndex(transcript: readonly TranscriptItem[]): number {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const item = transcript[i]!;
    if (item.kind === 'message' && item.message.role === 'player') {
      return i;
    }
  }
  return -1;
}

export function ChatOverlay({
  transcript,
  phase,
  awaitingFirstOutput = false,
  noOutputSettle = false,
  statusLine,
  scopeTypeName,
  headerRemainder,
  onSend,
  onStop,
  onClose,
  onRefresh,
  onClear,
}: ChatOverlayProps) {
  const [text, setText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  // Whether new content pulls the view down with it. A ref because the append effect must read the
  // stickiness as it was *before* the append (see the header note), and a state mirror because the
  // jump control's presence is rendered from it.
  const followRef = useRef(true);
  const [following, setFollowing] = useState(true);
  // Escape's "stop, THEN close" (client-beekeeper-chat): the running turn is not dismissed until
  // it has actually stopped, so this latches rather than closing immediately.
  const pendingAutoCloseRef = useRef(false);
  // Exactly `phase === 'running'` — the one phase with a turn id to stop. It now decides only the
  // FOOTER's Send-vs-Stop (**D-1**) and Escape's stop-first latch; the header's Close is
  // unconditional, so there is no longer a modal-vs-dismissable split for it to drive.
  const running = phase === 'running';
  // The composer is disabled for the WHOLE of busy, including `uncertain` — resending while an
  // outcome is unverified is exactly the duplicate-send risk `uncertain` exists to prevent.
  const busy = isChatBusy(phase);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return;
    }
    realFocusables(container)[0]?.focus();
  }, []);

  useEffect(() => {
    if (!running && pendingAutoCloseRef.current) {
      pendingAutoCloseRef.current = false;
      onClose();
    }
  }, [running, onClose]);

  function setFollow(next: boolean): void {
    followRef.current = next;
    setFollowing(next);
  }

  function scrollToLatest(): void {
    const el = transcriptRef.current;
    if (el === null) {
      return;
    }
    // Assigning the full `scrollHeight` rather than `scrollHeight - clientHeight`: the browser
    // clamps it, and naming the height means a fractional client box can never leave a sliver of
    // the newest message unread.
    el.scrollTop = el.scrollHeight;
  }

  // Mount is included deliberately — a re-entered thread opens on its newest message rather than at
  // the top of its history. The deps are everything that can lengthen the transcript's content: the
  // item list, and the "Thinking" placeholder's own two gates, since that row appears below the last
  // item without the list changing at all.
  useLayoutEffect(() => {
    if (followRef.current) {
      scrollToLatest();
    }
  }, [transcript, busy, awaitingFirstOutput]);

  function handleTranscriptScroll(): void {
    const el = transcriptRef.current;
    if (el === null) {
      return;
    }
    setFollow(distanceFromBottom(el) <= FOLLOW_SLACK_PX);
  }

  function handleJumpToLatest(): void {
    // Latched here rather than left to the `scroll` event this jump provokes: that event is async
    // and smooth-scroll would delay it further, and the control must not linger over the bottom of
    // a conversation the player has already been returned to.
    setFollow(true);
    scrollToLatest();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== 'Escape') {
      return;
    }
    e.preventDefault();
    if (running) {
      pendingAutoCloseRef.current = true;
      onStop();
    } else {
      onClose();
    }
  }

  function focusLast(): void {
    const container = containerRef.current;
    if (container === null) {
      return;
    }
    const items = realFocusables(container);
    items[items.length - 1]?.focus();
  }

  function focusFirst(): void {
    const container = containerRef.current;
    if (container === null) {
      return;
    }
    realFocusables(container)[0]?.focus();
  }

  /** Returns whether it actually sent, so `submitOnEnter` consumes the key only then (review
   * follow-up: an unconditional `preventDefault` left Enter dead on an empty draft). */
  function submit(): boolean {
    if (busy || text.trim().length === 0) {
      return false;
    }
    onSend(text);
    setText('');
    return true;
  }

  const scopedIndex =
    scopeTypeName !== undefined && scopeTypeName !== null && scopeTypeName.length > 0
      ? lastPlayerMessageIndex(transcript)
      : -1;

  return (
    <div
      ref={containerRef}
      data-testid="chat-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Beekeeper chat"
      // The dialog region carries `aria-busy` rather than a second live region — the status line
      // below is the surface's ONLY polite announcer (`beekeeper-progress-indicator` §2).
      aria-busy={busy}
      style={OVERLAY_STYLE}
      onKeyDown={handleKeyDown}
    >
      <div
        data-chat-focus-trap-sentinel=""
        data-testid="chat-focus-sentinel-start"
        tabIndex={0}
        onFocus={(e: FocusEvent) => {
          e.stopPropagation();
          focusLast();
        }}
        style={SENTINEL_STYLE}
      />

      <div data-testid="chat-header" style={HEADER_STYLE}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={TITLE_STYLE}>Beekeeper</div>
          {/* The surface's SINGLE polite live region (`beekeeper-progress-indicator` §2) — every
              phase change is announced exactly once, here. The bee is `aria-hidden`. */}
          <div
            data-testid="chat-status-line"
            role="status"
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-xs)',
              marginTop: 6,
              color: phaseStatusColor(phase),
            }}
          >
            {busy && (
              // ×2 = 32px (`busy-bee.html`'s own finding). Negative margins let the box overflow
              // the header's own line height without stretching the row — the sketch's own recipe
              // (`header-phases.html`). `uncertain` parks unanimated: nothing is verifiably moving.
              //
              // The bleed is deliberately **asymmetric**: the title above sits on `lineHeight: 1`
              // with no leading of its own, so an equal -8px/-8px overflow put the sprite's wing
              // into the word "Beekeeper". Taking 4px off the top and giving it to the bottom moves
              // the bee down without changing what it contributes to layout (32 − 4 − 12 = 16, the
              // same as 32 − 8 − 8), so the header does not change height between phases.
              <BusyBee animated={phase !== 'uncertain'} size={32} style={{ margin: '-4px -4px -12px -2px' }} />
            )}
            <span>{phaseStatusLine(phase, noOutputSettle) ?? statusLine ?? `Idle · ${messageCount(transcript)} messages`}</span>
          </div>
        </div>
        {headerRemainder}
        {phase === 'uncertain' && (
          <button
            data-testid="chat-refresh"
            onClick={onRefresh}
            style={{ ...HIT_TARGET_BUTTON, background: 'var(--accent-soft)', color: 'var(--accent-on-soft)', border: '1.5px solid var(--accent)' }}
          >
            Refresh conversation
          </button>
        )}
        {/* New chat (WAG-94) ends the conversation and starts a fresh one — the deliberate,
            destructive counterpart to Close below. It stays gated on `!busy`: clearing mid-turn
            is the one thing minimising is explicitly *not*. */}
        {onClear !== undefined && !busy && (
          <button
            data-testid="chat-clear"
            onClick={onClear}
            title="Clear this conversation and start a new one"
            style={{ ...HIT_TARGET_BUTTON, background: 'var(--surface-card)', color: 'var(--ink-2)', border: '1.5px solid var(--line-strong)' }}
          >
            New chat
          </button>
        )}
        {/* Close, unconditionally (**D-1**). It used to be the else-branch of a Stop/Close
            conditional, so a player who wanted to put the panel away found Stop instead. Stop now
            lives in the footer, and Close no longer ends the conversation — every mount wires it
            to its own `minimise` (**D-2**), which is why offering it mid-turn is safe. */}
        <button
          data-testid="chat-close"
          aria-label="Close the conversation — the Beekeeper keeps working"
          onClick={onClose}
          style={{ ...HIT_TARGET_BUTTON, background: 'var(--surface-card)', color: 'var(--ink-2)', border: '1.5px solid var(--line-strong)' }}
        >
          ✕ Close
        </button>
      </div>

      <div style={TRANSCRIPT_REGION_STYLE}>
        <div ref={transcriptRef} data-testid="chat-transcript" style={SCROLL_STYLE} onScroll={handleTranscriptScroll}>
          {transcript.map((item, index) => {
            if (item.kind === 'terminal') {
              return (
                <TerminalRow
                  key={`terminal-${item.terminal.turnId}-${index}`}
                  reason={item.terminal.reason}
                  version={item.terminal.version}
                />
              );
            }
            if (item.kind === 'tool') {
              return (
                <div key={`tool-${item.tool.callId}-${index}`} style={TOOL_COLUMN_STYLE}>
                  <ToolCallRow
                    callId={item.tool.callId}
                    tool={item.tool.tool}
                    status={item.tool.status}
                    summary={item.tool.summary}
                    detail={item.tool.detail}
                  />
                </div>
              );
            }
            const m = item.message;
            if (m.role === 'player') {
              // A Fragment, not a wrapper element: the pill and the bubble are two SIBLING rows of
              // the transcript's flex column (the pill's own -6px pulls them together), so wrapping
              // them would change the column's spacing.
              return (
                <Fragment key={m.id}>
                  {index === scopedIndex && (
                    <div data-testid="chat-scope-pill" style={SCOPE_PILL_STYLE}>
                      <span>{'Current selected type: '}</span>
                      <span data-testid="chat-scope-type" style={SCOPE_TYPE_STYLE}>
                        {scopeTypeName}
                      </span>
                    </div>
                  )}
                  <div data-testid="chat-message-player" style={PLAYER_BUBBLE_STYLE}>
                    {m.text}
                  </div>
                </Fragment>
              );
            }
            return (
              <div key={m.id} data-testid="chat-message-agent" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {m.text.length > 0 && (
                  <div style={AGENT_ROW_STYLE}>
                    <span aria-hidden="true" style={AGENT_MARKER_STYLE}>
                      🐝
                    </span>
                    <div style={AGENT_PROSE_STYLE}>
                      <AgentProse text={m.text} />
                    </div>
                  </div>
                )}
                {m.toolCalls.length > 0 && (
                  <div style={TOOL_COLUMN_STYLE}>
                    {m.toolCalls.map((t, toolIndex) => (
                      // Keyed off the PARENT message's own (cursor-derived, unique) id plus this
                      // row's position — never `callId` alone (E2E round-1 finding M-1, 2026-08-09):
                      // a malformed turn that starts two tool calls under the same `callId` before
                      // either finishes would otherwise hand React two rows with one React key.
                      <ToolCallRow
                        key={`${m.id}-tool-${toolIndex}`}
                        callId={t.callId}
                        tool={t.tool}
                        status={t.status}
                        summary={t.summary}
                        detail={t.detail}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {/* The "Thinking" placeholder (sketch variant **C**, decided 2026-08-10): no box — the bee
              simply appears in the transcript, so it can never be mistaken for a message that failed
              to render. Derived, never latched: `busy && awaitingFirstOutput` clears itself the
              moment ANY exit from busy happens, including a turn that settles with no output at all,
              so nothing here needs its own reset rule. It does not reappear between later tool calls
              — those rows carry their own running state. */}
          {busy && awaitingFirstOutput && (
            <div data-testid="chat-thinking-placeholder" style={THINKING_PLACEHOLDER_STYLE}>
              <BusyBee animated size={32} />
              <span style={THINKING_LABEL_STYLE}>Thinking</span>
              <span aria-hidden="true" style={THINKING_DOTS_STYLE}>
                <span style={THINKING_DOT_STYLE} />
                <span style={THINKING_DOT_STYLE} />
                <span style={THINKING_DOT_STYLE} />
              </span>
            </div>
          )}
        </div>

        {/* Absent, not disabled, while the player is already at the bottom: there is nothing to jump
            to, and a permanent button over the newest message would cover it. */}
        {!following && (
          <button
            data-testid="chat-jump-to-latest"
            aria-label="Jump to the latest message"
            title="Jump to the latest message"
            onClick={handleJumpToLatest}
            style={JUMP_TO_LATEST_STYLE}
          >
            <span aria-hidden="true">↓</span>
          </button>
        )}
      </div>

      <div data-testid="chat-footer" style={FOOTER_STYLE}>
        {/* The kit dims the field rather than hiding it while a turn runs: the player can still
            read what they are about to be able to say next. */}
        {/* `data-ui-field="composite"` hands the frame to the shell (`ui/app-shell.css`): this
            wrapper is the box the player sees, so the textarea inside it must not draw a second,
            smaller ring of its own. The prompt bar behind this popup delegates identically, which
            is what keeps the two fields looking like one control in two places. */}
        <div data-ui-field="composite" style={{ ...FIELD_STYLE, opacity: busy ? 0.45 : 1 }}>
          {/* Enter sends, Shift+Enter inserts a newline (UI/UX audit **F-02**). The rule lives in
              `ui/keyboard-policy.ts` so this input and the sandbox prompt bar cannot drift apart,
              and it calls the same guarded `submit()` the Send button does — so a keyboard send can
              never bypass the busy/empty checks. Composer is disabled for the whole of `busy`
              (`beekeeper-progress-indicator` §2) — including `uncertain` — and re-enabled only in
              `failed`, where resending is the retry mechanism. */}
          <textarea
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={submitOnEnter(submit)}
            placeholder={running ? RUNNING_PLACEHOLDER : busy ? BUSY_PLACEHOLDER : IDLE_PLACEHOLDER}
            disabled={busy}
            rows={1}
            // The frame around this composer owns its padding and height; the shell's
            // standalone-control metrics would indent the text and stand a one-line composer
            // 44px tall inside a frame already exactly that (`ui/app-shell.css`).
            data-ui-field-control=""
            style={INPUT_STYLE}
          />
        </div>
        {/* **Stop replaces Send while a turn runs** (`player-chat-and-outcome` **D-1**): the
            button under the textarea is where the player's hands and eyes already are, so it is
            where the turn is stopped. Deliberately NOT a relabel of one button — the two carry
            different destructive weight, so Stop gets its own element, its own danger styling and
            its own `aria-label`, and a screen reader announces a change of action rather than a
            change of word. Both `data-testid`s are the ones the header used to carry, so every
            existing binding follows the control to its new position. */}
        {running ? (
          <button
            data-testid="chat-stop"
            aria-label="Stop the Beekeeper"
            onClick={onStop}
            style={{
              ...HIT_TARGET_BUTTON,
              background: 'var(--danger-soft)',
              color: 'var(--danger)',
              border: '1.5px solid var(--danger)',
            }}
          >
            Stop
          </button>
        ) : (
          <button
            data-testid="chat-send"
            disabled={busy || text.trim().length === 0}
            onClick={submit}
            style={{
              ...HIT_TARGET_BUTTON,
              background: 'var(--accent-fill)',
              color: '#FFFDF6',
              border: '1px solid var(--accent-strong)',
              opacity: busy || text.trim().length === 0 ? 0.38 : 1,
            }}
          >
            Send
          </button>
        )}
      </div>

      <div
        data-chat-focus-trap-sentinel=""
        data-testid="chat-focus-sentinel-end"
        tabIndex={0}
        onFocus={(e: FocusEvent) => {
          e.stopPropagation();
          focusFirst();
        }}
        style={SENTINEL_STYLE}
      />
    </div>
  );
}
